import { useQuery } from '@tanstack/react-query'
import { MapPin, Calendar, Plane } from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { PageHeader } from '@/components/PageHeader'
import { TravelDeskChat } from '@/components/TravelDeskChat'

interface TravelLeg {
  id: string
  artistId: string
  showId: string | null
  type: string
  origin: string
  destination: string
  departureAt: string
  arrivalAt: string | null
  carrier: string | null
  flightNumber: string | null
  aircraft: string | null
  isEmptyLeg: boolean
  costPence: number | null
  bookingRef: string | null
  partyCount: number
}

export function CrewItinerariesPage() {
  const { data, isLoading } = useQuery<TravelLeg[], ApiClientError>({
    queryKey: ['crew-itineraries'],
    queryFn: () => apiFetch('/crew/profile/itineraries'),
  })

  const items = data ?? []

  return (
    <div>
      <PageHeader
        title="Itineraries"
        subtitle="Your upcoming travel plans and show schedules"
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-ovation-ink/20 border-t-ovation-ink" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-center text-sm text-ovation-muted">
          <MapPin className="mx-auto mb-2 h-8 w-8" />
          No upcoming itineraries. Accept engagement offers to see your travel here.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((leg) => (
            <div
              key={leg.id}
              className="rounded-lg border border-ovation-ink/10 bg-white p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-ovation-muted">
                    {leg.type.replace(/_/g, ' ')}
                    {leg.carrier && ` — ${leg.carrier}`}
                    {leg.flightNumber && ` ${leg.flightNumber}`}
                  </div>
                  <div className="mt-1 flex items-center gap-2 font-display text-lg">
                    <span>{leg.origin}</span>
                    <Plane className="h-4 w-4 text-ovation-muted" />
                    <span>{leg.destination}</span>
                  </div>
                  {leg.bookingRef && (
                    <div className="mt-0.5 text-xs text-ovation-muted">
                      Ref: {leg.bookingRef}
                    </div>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <div className="flex items-center gap-1 text-sm text-ovation-ink">
                    <Calendar className="h-3 w-3" />
                    {new Date(leg.departureAt).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-ovation-muted">
                    {new Date(leg.departureAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {leg.arrivalAt && (
                      <> — {new Date(leg.arrivalAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8">
        <TravelDeskChat mode="crew" />
      </div>
    </div>
  )
}
